import * as fflate from 'fflate';

/**
 * Concatenate per-slice SDF pixel arrays into one contiguous buffer. Assumes a
 * uniform slice length (every SDF slice spans the same grid), matching how the
 * snapshot is later split back apart on decompress. Caller must pass a non-empty
 * array.
 */
export function concatSlices(slices: Uint8Array[]): Uint8Array {
    const sliceSize = slices[0].length;
    const buffer = new Uint8Array(slices.length * sliceSize);
    for (let z = 0; z < slices.length; z++) {
        buffer.set(slices[z], z * sliceSize);
    }
    return buffer;
}

/** The z-major buffer's slices, copied: the inverse of {@link concatSlices}. */
export function unflattenToSlices(
    flat: Uint8Array,
    sizeX: number,
    sizeY: number,
    sizeZ: number
): Uint8Array[] {
    const sliceLen = sizeX * sizeY;
    return Array.from({ length: sizeZ }, (_, z) =>
        flat.slice(z * sliceLen, (z + 1) * sliceLen)
    );
}

/**
 * Compress per-slice SDF pixels into a single buffer, running fflate in a Web
 * Worker so the main thread is never blocked — the SDF is large and compressing
 * it synchronously janks the UI at the exact moment of an edit. Shared by the
 * undo history (`RoiMementoFactory`) and the original-AI baseline store
 * (`RoiBaselineStore`). Caller must pass a non-empty array.
 */
export function compressSlices(slices: Uint8Array[]): Promise<Uint8Array> {
    const concatenated = concatSlices(slices);
    return new Promise<Uint8Array>((resolve, reject) => {
        fflate.compress(concatenated, { level: 1 }, (error, data) => {
            if (error) {
                reject(error);
            } else {
                resolve(data);
            }
        });
    });
}
